/**
 * Tài khoản đang bị khoá tạm do đăng nhập sai nhiều lần (ADR-0059 mục 5.3).
 *
 * `GET /api/v1/tenants/{id}/locked-users` của user-service tra **theo khúc**:
 * mỗi yêu cầu tra một khúc hồ sơ, hỏi Keycloak từng người rồi trả người đang bị
 * khoá. Vì vậy một trang có thể rỗng mà vẫn còn `next_cursor` — "rỗng" chỉ
 * chắc chắn khi đã tra hết (`next_cursor` null).
 */

/** `LockedUser` — giữ đúng tên trường của API. */
export interface TaiKhoanKhoaTam {
  user_id: string;
  /** Email ở Keycloak; null khi không đọc được. */
  email: string | null;
  full_name: string;
  num_failures: number;
  last_failure: string | null;
  last_ip_failure: string | null;
}

/** `LockedUserPage`. */
export interface TrangKhoaTam {
  items: TaiKhoanKhoaTam[];
  next_cursor: string | null;
}

/**
 * Nối các trang đã tra thành một danh sách. Bỏ dòng trùng `user_id`: hồ sơ mới
 * tạo giữa hai lần tra đẩy khúc sau lùi lại, nên một người có thể xuất hiện ở
 * cả hai trang.
 */
export function gopTrangKhoaTam(trang: readonly TrangKhoaTam[]): TaiKhoanKhoaTam[] {
  const daCo = new Set<string>();
  const ra: TaiKhoanKhoaTam[] = [];
  for (const p of trang) {
    for (const d of p.items) {
      if (daCo.has(d.user_id)) continue;
      daCo.add(d.user_id);
      ra.push(d);
    }
  }
  return ra;
}

/**
 * Bỏ một người vừa mở khoá khỏi các trang trong cache, giữ nguyên con trỏ:
 * tải lại từ đầu thì mất phần đã tra.
 */
export function boKhoiTrangKhoaTam<T extends { pages: TrangKhoaTam[] }>(du: T, userId: string): T {
  return {
    ...du,
    pages: du.pages.map((p) =>
      p.items.some((d) => d.user_id === userId)
        ? { ...p, items: p.items.filter((d) => d.user_id !== userId) }
        : p,
    ),
  };
}

/** Trạng thái vùng dữ liệu khi đã tải xong lần đầu, chưa tính lỗi. */
export type TinhTrangKhoaTam = 'co-du-lieu' | 'chua-thay' | 'rong';

/**
 * - `co-du-lieu`: đã có ít nhất một người.
 * - `chua-thay`: phần đã tra chưa có ai nhưng còn hồ sơ chưa tra — **không**
 *   được báo "không có ai".
 * - `rong`: đã tra hết, không ai bị khoá.
 */
export function tinhTrangKhoaTam(soDong: number, conTrang: boolean): TinhTrangKhoaTam {
  if (soDong > 0) return 'co-du-lieu';
  return conTrang ? 'chua-thay' : 'rong';
}
